import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  FaMoneyBillWave,
  FaSearch,
  FaCheck,
  FaTimes,
  FaEye,
  FaClock,
  FaUser,
  FaCalendarAlt,
  FaSync,
  FaExclamationTriangle,
  FaCheckCircle,
  FaUpload,
  FaImage,
} from "react-icons/fa";
import { toast } from "react-toastify";
import axios from "axios";
import { API_URL } from "../../config/apiConfig";

const PendingWithdrawals = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [approvalData, setApprovalData] = useState({
    referenceId: "",
    notes: "",
    paymentProofImage: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const { user: adminUser } = useSelector((state) => state.auth);

  useEffect(() => {
    fetchPendingWithdrawals();
  }, []);

  const fetchPendingWithdrawals = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("adminToken") || adminUser?.token;

      // Fallback to using the existing drivers endpoint with filter
      const response = await axios.get(`${API_URL}/wallet/admin/drivers`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { filter: "withdrawal" },
      });

      if (response.data.success) {
        const driversWithWithdrawals = response.data.data.drivers.filter(
          (driver) => driver.pendingWithdrawal?.amount > 0,
        );
        setWithdrawals(driversWithWithdrawals);
      }
    } catch (error) {
      console.error("Error fetching pending withdrawals:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const handleImageUpload = async (file) => {
    if (!file) return null;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('paymentProof', file);

      const token = localStorage.getItem("adminToken") || adminUser?.token;

      // Upload to a temporary endpoint or cloud storage
      // For now, we'll use a simple base64 conversion
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image");
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleApproveWithdrawal = async (driverId, amount) => {
    if (!approvalData.referenceId.trim()) {
      toast.error("Please enter a reference ID");
      return;
    }

    try {
      setProcessing(driverId);
      const token = localStorage.getItem("adminToken") || adminUser?.token;

      let paymentProofImage = null;
      if (approvalData.paymentProofImage) {
        paymentProofImage = await handleImageUpload(approvalData.paymentProofImage);
      }

      const response = await axios.post(
        `${API_URL}/wallet/admin/approve-withdrawal`,
        {
          driverId,
          amount,
          referenceId: approvalData.referenceId,
          adminId: adminUser._id,
          paymentProofImage,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        toast.success("Withdrawal approved successfully");
        setApprovalData({ referenceId: "", notes: "", paymentProofImage: null });
        setImagePreview(null);
        setShowModal(false);
        setSelectedWithdrawal(null);
        fetchPendingWithdrawals(); // Refresh list
      }
    } catch (error) {
      console.error("Error approving withdrawal:", error);
      toast.error(
        error.response?.data?.message || "Failed to approve withdrawal",
      );
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectWithdrawal = async (driverId, amount) => {
    try {
      setProcessing(driverId);
      const token = localStorage.getItem("adminToken") || adminUser?.token;

      // For now, we'll implement rejection by clearing the pending withdrawal
      // You may want to add a proper reject endpoint later
      const response = await axios.post(
        `${API_URL}/wallet/admin/reject-withdrawal`,
        {
          driverId,
          amount,
          adminId: adminUser._id,
          reason: approvalData.notes || "Rejected by admin",
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        toast.success("Withdrawal rejected");
        setApprovalData({ referenceId: "", notes: "", paymentProofImage: null });
        setImagePreview(null);
        setShowModal(false);
        setSelectedWithdrawal(null);
        fetchPendingWithdrawals(); // Refresh list
      }
    } catch (error) {
      console.error("Error rejecting withdrawal:", error);
      toast.error(
        error.response?.data?.message || "Failed to reject withdrawal",
      );
    } finally {
      setProcessing(null);
    }
  };

  const openApprovalModal = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setShowModal(true);
    setApprovalData({ referenceId: "", notes: "", paymentProofImage: null });
    setImagePreview(null);
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error("Image size should be less than 5MB");
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast.error("Please select a valid image file");
        return;
      }

      setApprovalData(prev => ({ ...prev, paymentProofImage: file }));

      // Create preview
      const reader = new FileReader();
      reader.onload = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const filteredWithdrawals = withdrawals.filter(
    (withdrawal) =>
      withdrawal.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      withdrawal.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      withdrawal.phone?.includes(searchTerm),
  );

  return (
    <div className="w-full px-2 sm:px-4 md:px-6 lg:px-8 py-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
        <div className="flex items-center mb-4 sm:mb-0">
          <FaMoneyBillWave className="text-3xl text-yellow-600 mr-3" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Pending Withdrawals
            </h1>
            <p className="text-gray-600 mt-1">
              Review and approve driver withdrawal requests
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={fetchPendingWithdrawals}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FaSync className="mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Requests</p>
              <p className="text-2xl font-bold text-yellow-600">
                {withdrawals.length}
              </p>
            </div>
            <FaClock className="text-2xl text-yellow-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Amount</p>
              <p className="text-2xl font-bold text-green-600">
                {formatCurrency(
                  withdrawals.reduce(
                    (sum, w) => sum + (w.pendingWithdrawal?.amount || 0),
                    0,
                  ),
                )}
              </p>
            </div>
            <FaMoneyBillWave className="text-2xl text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Average Request</p>
              <p className="text-2xl font-bold text-blue-600">
                {withdrawals.length > 0
                  ? formatCurrency(
                      withdrawals.reduce(
                        (sum, w) => sum + (w.pendingWithdrawal?.amount || 0),
                        0,
                      ) / withdrawals.length,
                    )
                  : formatCurrency(0)}
              </p>
            </div>
            <FaUser className="text-2xl text-blue-600" />
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6 border border-gray-200">
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by driver name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Withdrawals List */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Withdrawal Requests ({filteredWithdrawals.length})
          </h3>
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredWithdrawals.length === 0 ? (
          <div className="text-center py-12">
            <FaCheckCircle className="mx-auto text-6xl text-green-500 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              No Pending Withdrawals
            </h3>
            <p className="text-gray-600">
              All withdrawal requests have been processed.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredWithdrawals.map((withdrawal) => (
              <div
                key={withdrawal._id || withdrawal.driverId}
                className="p-6 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0 h-12 w-12">
                      <div className="h-12 w-12 rounded-full bg-yellow-100 flex items-center justify-center">
                        <FaUser className="text-yellow-600" />
                      </div>
                    </div>
                    <div>
                      <h4 className="text-lg font-medium text-gray-900">
                        {withdrawal.name || "Unknown Driver"}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {withdrawal.email} • {withdrawal.phone}
                      </p>
                      <p className="text-sm text-gray-500">
                        Vehicle: {withdrawal.vehicleNumber || "N/A"}
                      </p>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-600 mb-1">
                      {formatCurrency(withdrawal.pendingWithdrawal?.amount)}
                    </div>
                    <div className="text-sm text-gray-500 mb-2">
                      <FaCalendarAlt className="inline mr-1" />
                      Requested:{" "}
                      {withdrawal.pendingWithdrawal?.requestDate
                        ? new Date(
                            withdrawal.pendingWithdrawal.requestDate,
                          ).toLocaleDateString()
                        : "N/A"}
                    </div>
                    <div className="text-sm text-gray-600">
                      Current Balance: {formatCurrency(withdrawal.balance)}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => openApprovalModal(withdrawal)}
                      className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      disabled={processing === withdrawal.driverId}
                    >
                      {processing === withdrawal.driverId ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                      ) : (
                        <FaCheck className="mr-2" />
                      )}
                      Approve
                    </button>

                    <button
                      onClick={() => openApprovalModal(withdrawal)}
                      className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                      disabled={processing === withdrawal.driverId}
                    >
                      <FaTimes className="mr-2" />
                      Reject
                    </button>

                    <button
                      onClick={() => openApprovalModal(withdrawal)}
                      className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      title="View Details"
                    >
                      <FaEye />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Approval Modal */}
      {showModal && selectedWithdrawal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Process Withdrawal Request
              </h3>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <h4 className="font-medium text-gray-900 mb-2">
                  Driver: {selectedWithdrawal.name}
                </h4>
                <p className="text-sm text-gray-600 mb-2">
                  Amount: {formatCurrency(selectedWithdrawal.pendingWithdrawal?.amount)}
                </p>
                <p className="text-sm text-gray-600">
                  Current Balance: {formatCurrency(selectedWithdrawal.balance)}
                </p>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reference ID *
                </label>
                <input
                  type="text"
                  value={approvalData.referenceId}
                  onChange={(e) =>
                    setApprovalData((prev) => ({
                      ...prev,
                      referenceId: e.target.value,
                    }))
                  }
                  placeholder="Enter transaction reference ID"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Proof Image (Optional)
                </label>
                <div className="space-y-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                    id="payment-proof-upload"
                  />
                  <label
                    htmlFor="payment-proof-upload"
                    className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <FaUpload className="mr-2 text-gray-500" />
                    <span className="text-sm text-gray-700">
                      {approvalData.paymentProofImage ? 'Change Image' : 'Upload Payment Proof'}
                    </span>
                  </label>

                  {imagePreview && (
                    <div className="relative">
                      <img
                        src={imagePreview}
                        alt="Payment proof preview"
                        className="w-full h-32 object-cover rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setApprovalData(prev => ({ ...prev, paymentProofImage: null }));
                          setImagePreview(null);
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={approvalData.notes}
                  onChange={(e) =>
                    setApprovalData((prev) => ({
                      ...prev,
                      notes: e.target.value,
                    }))
                  }
                  placeholder="Add any notes about this transaction"
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex items-center space-x-3">
                <button
                  onClick={() =>
                    handleApproveWithdrawal(
                      selectedWithdrawal.driverId,
                      selectedWithdrawal.pendingWithdrawal?.amount,
                    )
                  }
                  disabled={processing || uploading || !approvalData.referenceId.trim()}
                  className="flex-1 flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {processing || uploading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <FaCheck className="mr-2" />
                      Approve
                    </>
                  )}
                </button>

                <button
                  onClick={() =>
                    handleRejectWithdrawal(
                      selectedWithdrawal.driverId,
                      selectedWithdrawal.pendingWithdrawal?.amount,
                    )
                  }
                  disabled={processing}
                  className="flex-1 flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <FaTimes className="mr-2" />
                  Reject
                </button>

                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingWithdrawals;
